import { useState } from "react";
import { postLoginPath } from "@shared/auth/safe-return-path";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiRequest } from "@/lib/queryClient";
import { queryClient } from "@/lib/queryClient";
import { TurnstileWidget } from "@/components/security/turnstile-widget";
import { useTurnstileConfig } from "@/lib/turnstile";

function redirectAfterLogin() {
  const returnTo = new URLSearchParams(window.location.search).get("returnTo");
  window.location.assign(postLoginPath(returnTo));
}

export default function LoginPage() {
  const searchParams = new URLSearchParams(window.location.search);
  const needs2FA = searchParams.get("2fa") === "true";

  // If redirected back with 2fa=true, show 2FA page
  if (needs2FA) {
    return <TwoFactorForm />;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">HMS</CardTitle>
          <CardDescription>
            Sign in to your Hosting Management System
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="password" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="password">Password</TabsTrigger>
              <TabsTrigger value="magic-link">Magic Link</TabsTrigger>
            </TabsList>
            <TabsContent value="password">
              <PasswordForm />
            </TabsContent>
            <TabsContent value="magic-link">
              <MagicLinkForm />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

function PasswordForm() {
  const turnstile = useTurnstileConfig();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);

  if (needs2FA) {
    return <TwoFactorForm />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (turnstile.required && !turnstile.configured) {
      setError("Verification is temporarily unavailable. Please try again later.");
      return;
    }

    if ((turnstile.required || turnstile.enabled) && !turnstileToken) {
      setError("Please complete the verification step.");
      return;
    }

    setLoading(true);

    try {
      const res = await apiRequest("POST", "/api/auth/login", {
        email,
        password,
        // The login schema requires a string. When Turnstile is off, the
        // server ignores the token, so send a placeholder instead of null.
        turnstileToken: turnstileToken ?? "not-required",
      });
      const data = await res.json();

      if (data.requiresTwoFactor) {
        setNeeds2FA(true);
        return;
      }

      // Login successful - invalidate auth query and redirect
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      redirectAfterLogin();
    } catch (err: any) {
      const message = String(err.message || "");
      setError(
        message.includes("401")
          ? "Invalid email or password"
          : message.includes("temporarily unavailable")
            ? "Verification is temporarily unavailable. Please try again later."
          : message.includes("Please complete the verification step")
            ? "Please complete the verification step."
          : message.includes("Verification")
              ? "Verification failed. Please try again."
              : "Something went wrong. Please try again.",
      );
    } finally {
      setTurnstileResetKey((value) => value + 1);
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
        />
      </div>
      {turnstile.enabled && turnstile.siteKey ? (
        <TurnstileWidget
          siteKey={turnstile.siteKey}
          action="login_password"
          onTokenChange={setTurnstileToken}
          resetKey={turnstileResetKey}
        />
      ) : null}
      {turnstile.required && !turnstile.configured ? (
        <p className="text-sm text-destructive">
          Verification is temporarily unavailable. Please try again later.
        </p>
      ) : null}
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
      <Button
        type="submit"
        className="w-full"
        disabled={loading || (turnstile.required && !turnstile.configured)}
      >
        {loading ? "Signing in..." : "Sign in"}
      </Button>
    </form>
  );
}

function MagicLinkForm() {
  const turnstile = useTurnstileConfig();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (turnstile.required && !turnstile.configured) {
      setError("Verification is temporarily unavailable. Please try again later.");
      return;
    }

    if ((turnstile.required || turnstile.enabled) && !turnstileToken) {
      setError("Please complete the verification step.");
      return;
    }

    setLoading(true);

    try {
      const returnTo = new URLSearchParams(window.location.search).get("returnTo");
      await apiRequest("POST", "/api/auth/magic-link/request", {
        email,
        turnstileToken,
        ...(returnTo ? { returnTo } : {}),
      });
      setSent(true);
    } catch (err: any) {
      const message = String(err.message || "");
      setError(
        message.includes("temporarily unavailable")
          ? "Verification is temporarily unavailable. Please try again later."
          : message.includes("Please complete the verification step")
          ? "Please complete the verification step."
          : message.includes("Verification")
            ? "Verification failed. Please try again."
            : "Something went wrong. Please try again.",
      );
    } finally {
      setTurnstileResetKey((value) => value + 1);
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center py-6 space-y-2">
        <p className="text-lg font-medium">Check your email</p>
        <p className="text-sm text-muted-foreground">
          If an account exists for <strong>{email}</strong>, we've sent a sign-in
          link.
        </p>
        <Button
          variant="ghost"
          className="mt-4"
          onClick={() => setSent(false)}
        >
          Try a different email
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <div className="space-y-2">
        <Label htmlFor="magic-email">Email</Label>
        <Input
          id="magic-email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      {turnstile.enabled && turnstile.siteKey ? (
        <TurnstileWidget
          siteKey={turnstile.siteKey}
          action="login_magic_link"
          onTokenChange={setTurnstileToken}
          resetKey={turnstileResetKey}
        />
      ) : null}
      {turnstile.required && !turnstile.configured ? (
        <p className="text-sm text-destructive">
          Verification is temporarily unavailable. Please try again later.
        </p>
      ) : null}
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
      <Button
        type="submit"
        className="w-full"
        disabled={loading || (turnstile.required && !turnstile.configured)}
      >
        {loading ? "Sending..." : "Send magic link"}
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        We'll email you a link to sign in without a password.
      </p>
    </form>
  );
}

function TwoFactorForm() {
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await apiRequest("POST", "/api/auth/verify-2fa", { token });
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      redirectAfterLogin();
    } catch (err: any) {
      setError(err.message?.includes("401")
        ? "Invalid code. Please try again."
        : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Two-Factor Auth</CardTitle>
          <CardDescription>
            Enter the 6-digit code from your authenticator app
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="2fa-token">Verification Code</Label>
              <Input
                id="2fa-token"
                type="text"
                inputMode="numeric"
                placeholder="000000"
                value={token}
                onChange={(e) => setToken(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
                maxLength={6}
                className="text-center text-2xl tracking-widest"
              />
            </div>
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
            <Button type="submit" className="w-full" disabled={loading || token.length !== 6}>
              {loading ? "Verifying..." : "Verify"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              You can also enter a backup code.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
